import React from 'react';
import { type MRT_ColumnDef, MaterialReactTable } from 'material-react-table';
import { useMRTStyling } from '../../hooks/useMRTStyling';
import { Division } from '../glossary/glossary.type';

interface DivisionsListProps {
  divisions: Division[];
  selectedDivisionIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  height?: number;
  useCustomHeader?: boolean;
}

const DivisionsList: React.FC<DivisionsListProps> = ({
  divisions,
  selectedDivisionIds,
  onSelectionChange,
  height = 580,
  useCustomHeader = false,
}) => {
  // Prepare data rows
  const rows = React.useMemo(
    () => [...divisions].sort((a, b) => Number(a.code) - Number(b.code)).map(d => ({ id: d.id, code: d.code, name: d.name })),
    [divisions],
  );

  // MRT columns
  const columns = React.useMemo<MRT_ColumnDef<(typeof rows)[number]>[]>(
    () => [
      {
        accessorKey: 'code',
        header: 'Code',
        size: 30,
      },
      {
        accessorKey: 'name',
        header: 'Division Name',
        grow: true,
      },
    ],
    [],
  );

  // Selection state mapping
  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});
  const [sorting, setSorting] = React.useState<any>([]);
  const { getOnSortingChangeWithToast } = useMRTStyling<any>();

  // Sync incoming selected ids to MRT selection state
  React.useEffect(() => {
    const mapped: Record<string, boolean> = {};
    for (const id of selectedDivisionIds) {
      mapped[id] = true;
    }
    setRowSelection(mapped);
  }, [selectedDivisionIds]);

  // When selection changes, surface selected ids
  const handleRowSelectionChange = React.useCallback(
    (updater: any) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(next);
      const selectedIds = Object.keys(next).filter(k => next[k]);
      onSelectionChange(selectedIds);
    },
    [rowSelection, onSelectionChange],
  );

  return (
    <MaterialReactTable
      columns={columns}
      data={rows}
      getRowId={row => row.id}
      layoutMode='grid'
      enableTopToolbar={false}
      enableBottomToolbar={false}
      enablePagination={false}
      enableColumnActions={false}
      enableColumnFilters={false}
      enableDensityToggle={false}
      enableFullScreenToggle={false}
      enableRowSelection
      enableMultiRowSelection
      state={{ rowSelection, density: 'compact', sorting }}
      onRowSelectionChange={handleRowSelectionChange}
      onSortingChange={getOnSortingChangeWithToast(sorting as any, setSorting as any)}
      enableStickyHeader
      displayColumnDefOptions={{
        'mrt-row-select': {
          size: 50,
          muiTableHeadCellProps: {
            sx: {
              padding: '0 8px',
              backgroundColor: theme => theme.palette.primary.main + ' !important',
              color: '#ffffff',
              '&::after': { display: 'none' },
              border: 'none',
              display: 'flex',
              justifyContent: 'center',
            },
          },
          muiTableBodyCellProps: ({ row }: any) => {
            const backgroundColor = row?.index % 2 === 0 ? '#ffffff' : '#f9f9f9';
            return {
              sx: {
                padding: '0 8px',
                backgroundColor: backgroundColor + ' !important',
                border: 'none',
                '&[data-pinned="true"]::before': {
                  backgroundColor: backgroundColor,
                },
              },
            };
          },
        },
      }}
      muiTableContainerProps={{
        sx: {
          flex: '1 1 auto',
          maxHeight: height,
          height: height,
          overflow: 'auto',
          backgroundColor: theme => theme.palette.background.paper,
        },
      }}
      muiTablePaperProps={{
        sx: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '8px',
        },
      }}
      muiTableProps={{
        sx: {
          borderRadius: 'inherit',
          marginTop: '0px',
        },
      }}
      muiTableHeadProps={{
        sx: {
          padding: 0,
          color: 'white',
          position: 'sticky',
          top: 0,
          zIndex: 2,
          backgroundColor: theme => theme.palette.primary.main,
        },
      }}
      muiTableHeadRowProps={{
        sx: {
          borderBottom: 'none',
        },
      }}
      muiTableHeadCellProps={{
        sx: theme => ({
          backgroundColor: theme.palette.primary.main + ' !important',
          color: '#ffffff',
          justifyContent: 'center',
          padding: '12px 9px',
          minHeight: '48px',
          border: 'none',
          position: 'sticky',
          top: 0,
          zIndex: 2,
          '&:not(:last-of-type)::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: '20%',
            height: '60%',
            width: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
          },
        }),
      }}
      muiTableBodyRowProps={({ row }) => {
        const backgroundColor = row.index % 2 === 0 ? '#ffffff' : '#f9f9f9';
        return {
          sx: {
            borderBottom: 'none',
            backgroundColor: backgroundColor + ' !important',
            '& td[data-pinned="true"]::before': {
              backgroundColor: backgroundColor,
            },
          },
        };
      }}
      muiTableBodyCellProps={{
        sx: {
          border: 'none',
          borderBottom: '0 !important',
          backgroundColor: 'transparent !important',
        },
      }}
    />
  );
};

export default DivisionsList;
